import React from 'react';
import {
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';

import InputField from '@/components/atoms/InputField';
import SelectField from '@/components/atoms/SelectField';
import {
  BUDGET_MEMBER_TYPE_OPTIONS,
  MEMBER_ROLE_ACTION_OPTIONS,
  MEMBER_ROLE_LABELS
} from '@/components/organisms/budget/budgetMember.constants';
import {
  BudgetMemberEditFormValues,
  BudgetMemberFormValues,
  BudgetMembersListFormValues
} from '@/components/organisms/budget/form-steps/budgetStep.schema';
import defaultUserAvatar from '@/assets/images/default-user.png';

interface BudgetMembersListProps {
  variant?: 'create' | 'edit';
  register: UseFormRegister<any>;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  errors: FieldErrors<any>;
  fields: FieldArrayWithId<BudgetMembersListFormValues, 'members', 'id'>[];
  remove: UseFieldArrayRemove;
  currentUserEmail?: string;
  currentUserPhotoUrl?: string;
  currency?: string;
  showTitle?: boolean;
  updatingMemberId?: string | null;
  onRoleChange?: (index: number, memberId: string, role: 'validation' | 'staff') => void | Promise<void>;
  onRemoveMember?: (index: number, memberId: string) => void | Promise<void>;
}

const toSelectValue = (value: string | string[]): string => (Array.isArray(value) ? (value[0] ?? '') : value);

const BudgetMembersList: React.FC<BudgetMembersListProps> = ({
  variant = 'create',
  register,
  watch,
  setValue,
  errors,
  fields,
  remove,
  currentUserEmail,
  currentUserPhotoUrl,
  currency = 'EUR',
  showTitle = true,
  updatingMemberId = null,
  onRoleChange,
  onRemoveMember
}) => {
  const currencySymbol = currency === 'USD' ? '$' : currency === 'GBP' ? '£' : '€';

  const getMemberAvatarSrc = (member: BudgetMemberFormValues | undefined, isCurrentUser: boolean) => {
    if (member?.profilePicUrl) {
      return member.profilePicUrl;
    }

    if (isCurrentUser && currentUserPhotoUrl) {
      return currentUserPhotoUrl;
    }

    return defaultUserAvatar;
  };

  const getMemberError = (index: number, field: keyof BudgetMemberFormValues) => {
    const membersErrors = errors.members as FieldErrors<BudgetMemberFormValues>[] | undefined;
    return membersErrors?.[index]?.[field];
  };

  const handleRoleChange = (index: number, value: string, memberRole: BudgetMemberEditFormValues['role']) => {
    if (memberRole === 'owner') return;

    const member = watch(`members.${index}`);

    if (variant === 'create' && value === 'remove_access') {
      remove(index);
      return;
    }

    if (variant === 'edit' && value === 'remove_access') {
      if (member?.memberId && onRemoveMember) {
        void onRemoveMember(index, member.memberId);
      }
      return;
    }

    if (variant === 'edit' && (value === 'validation' || value === 'staff') && member?.memberId && onRoleChange) {
      void onRoleChange(index, member.memberId, value);
      return;
    }

    setValue(`members.${index}.role`, value as Exclude<BudgetMemberEditFormValues['role'], 'owner'>, {
      shouldDirty: true,
      shouldValidate: true
    });
  };

  return (
    <div className="space-y-4">
      {showTitle && <h4 className="text-sm font-bold">Team Members</h4>}

      <div className="space-y-[22px] md:space-y-4">
        {fields.map((field, index) => {
          const member = watch(`members.${index}`);
          const isOwner = member?.role === 'owner';
          const isUpdating = !!member?.memberId && updatingMemberId === member.memberId;
          const isCurrentUser = !!currentUserEmail && member?.email?.toLowerCase() === currentUserEmail.toLowerCase();

          return (
            <div key={field.id} className={`space-y-2 ${isUpdating ? 'opacity-50 pointer-events-none' : ''}`}>
              <header className="flex items-center justify-between gap-2">
                <p className="text-xs truncate">
                  {member?.email}
                  {isCurrentUser && <span className="text-secondary"> (You)</span>}
                </p>

                <div className="md:hidden shrink-0">
                  {isOwner ? (
                    <span className="text-primary font-medium text-xs">{MEMBER_ROLE_LABELS.owner}</span>
                  ) : (
                    <SelectField
                      name={`members.${index}.role`}
                      placeholder="Select role"
                      options={[...MEMBER_ROLE_ACTION_OPTIONS]}
                      value={watch(`members.${index}.role`) ?? ''}
                      onChange={(value) => handleRoleChange(index, toSelectValue(value), member?.role ?? 'staff')}
                      error={getMemberError(index, 'role')}
                      size="sm"
                      variant="text"
                      buttonClassName="text-primary text-xs font-medium min-w-[72px]"
                    />
                  )}
                </div>
              </header>

              <div className="grid grid-cols-[40px_1fr] gap-2 md:flex md:items-start md:gap-2">
                <img
                  src={getMemberAvatarSrc(member, isCurrentUser)}
                  alt={member?.memberName || 'Member'}
                  className="col-start-1 row-start-1 size-10 rounded-full object-cover bg-lightGray shrink-0 md:col-auto md:row-auto"
                  onError={(event) => {
                    event.currentTarget.src = defaultUserAvatar;
                  }}
                />

                {variant === 'edit' ? (
                  <InputField
                    name={`members.${index}.memberName`}
                    placeholder="Member name"
                    type="text"
                    register={register}
                    error={getMemberError(index, 'memberName')}
                    size="sm"
                    className="col-start-2 row-start-1 md:col-auto md:row-auto md:flex-1"
                    inputClassName="disabled:placeholder:text-[#ADB5BD] disabled:opacity-80"
                    disabled
                  />
                ) : (
                  <InputField
                    name={`members.${index}.memberName`}
                    placeholder={member?.memberName ? 'Member name' : 'Enter email'}
                    type="text"
                    register={register}
                    error={getMemberError(index, 'memberName')}
                    size="sm"
                    className="col-start-2 row-start-1 md:col-auto md:row-auto md:flex-1"
                    inputClassName="disabled:placeholder:text-[#ADB5BD] disabled:opacity-80"
                    disabled
                  />
                )}

                <SelectField
                  name={`members.${index}.type`}
                  placeholder="Select type"
                  options={BUDGET_MEMBER_TYPE_OPTIONS}
                  value={watch(`members.${index}.type`) ?? ''}
                  onChange={(value) =>
                    setValue(`members.${index}.type`, toSelectValue(value), {
                      shouldDirty: true,
                      shouldValidate: true
                    })
                  }
                  error={getMemberError(index, 'type')}
                  size="sm"
                  className="col-span-2 row-start-2 md:col-auto md:row-auto md:flex-1"
                  disabled={isUpdating}
                />

                <div className="col-span-2 row-start-3 grid grid-cols-2 gap-2 md:contents">
                  <InputField
                    name={`members.${index}.oreHours`}
                    placeholder="ore hours..."
                    type="number"
                    register={register}
                    error={getMemberError(index, 'oreHours')}
                    size="sm"
                    className="min-w-0 md:flex-1"
                    unitDescription="Hours"
                    min={0}
                    disabled={isUpdating}
                  />

                  <InputField
                    name={`members.${index}.hourlyRate`}
                    placeholder="per hour rate..."
                    type="number"
                    register={register}
                    error={getMemberError(index, 'hourlyRate')}
                    size="sm"
                    className="min-w-0 md:flex-1"
                    inputClassName="disabled:placeholder:text-[#ADB5BD] disabled:opacity-80"
                    unitDescription={
                      <p>
                        <span className="font-bold">{currencySymbol}</span> /Hours
                      </p>
                    }
                    disabled
                    min={0}
                  />
                </div>

                <div className="hidden md:block shrink-0 min-w-[140px]">
                  {isOwner ? (
                    <p className="text-primary font-medium text-center text-sm pt-3">{MEMBER_ROLE_LABELS.owner}</p>
                  ) : (
                    <SelectField
                      name={`members.${index}.role`}
                      placeholder="Select role"
                      options={[...MEMBER_ROLE_ACTION_OPTIONS]}
                      value={watch(`members.${index}.role`) ?? ''}
                      onChange={(value) => handleRoleChange(index, toSelectValue(value), member?.role ?? 'staff')}
                      error={getMemberError(index, 'role')}
                      size="sm"
                      variant="text"
                      buttonClassName="text-primary text-xs font-medium min-w-[72px]"
                      className="mx-auto flex justify-center"
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BudgetMembersList;
